"use client";

import { BidAskDepthWidget } from "./bid-ask-depth-widget";
import { EtfFlowChart, RoroRegimeChart } from "./charts";
import { LiquidationDashboard } from "./liquidation-dashboard";
import { LeverageRegimeWidget } from "./leverage-regime-widget";
import { LongShortWidget } from "./long-short-widget";
import { EmptyState, SectionIntro } from "./module-panels";
import { FreshnessTag, Panel, PanelHeader } from "./ui";
import type { DashboardData, Point } from "@/lib/types";

function flowMetrics(points: Point[]) {
  const latest = points.at(-1)?.value ?? null;
  const recent = points.slice(-4);
  const average4d = recent.length ? recent.reduce((sum, point) => sum + point.value, 0) / recent.length : null;
  const cumulative30d = points.slice(-30).reduce((sum, point) => sum + point.value, 0);
  return { latest, average4d, cumulative30d: points.length ? cumulative30d : null };
}

function flowValue(value: number | null) {
  if (value == null) return "—";
  return `${value >= 0 ? "+" : "−"}$${Math.abs(value).toLocaleString("en", { maximumFractionDigits: 1 })}m`;
}

function FlowMetric({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--hc-muted)]">{label}</p>
      <p className={`mt-1 text-sm font-semibold tabular-nums ${value == null ? "text-[var(--hc-muted)]" : value >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{flowValue(value)}</p>
    </div>
  );
}

export function CryptoDashboard({ markets, crypto }: { markets: DashboardData["markets"]; crypto: DashboardData["crypto"] }) {
  const latestRoro = markets.roro.series.at(-1);
  const roroBreakdown = latestRoro?.breakdown ? Object.entries(latestRoro.breakdown) : [];
  const etfFlows = markets.etf_flows ?? [];
  const metrics = flowMetrics(etfFlows);

  return (
    <div className="space-y-10">
      <section id="crypto-overview" aria-labelledby="crypto-overview-heading" className="space-y-4">
        <SectionIntro
          id="crypto-overview-heading"
          eyebrow="Crypto liquidity"
          title="Risk regime and capital flows"
          description="Cross-asset regime, institutional ETF demand, spot order-book pressure, and leverage concentrations in one decision sequence."
        />

        <Panel>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-[var(--hc-text)]">{markets.roro.label}</h2>
              <p className="mt-1 text-xs text-[var(--hc-muted)]">{markets.roro.value}{markets.roro.score != null ? ` · score ${markets.roro.score}` : ""}</p>
            </div>
            <FreshnessTag status={markets.roro_source?.status} source={markets.roro_source?.name} collectedAt={markets.roro_source?.collected_at} />
          </div>
          {markets.roro.series.length ? <RoroRegimeChart series={markets.roro.series} /> : <EmptyState />}
          {roroBreakdown.length ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {roroBreakdown.map(([key, item]) => (
                <div key={key} className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] p-3">
                  <p className="text-xs font-semibold text-[var(--hc-text)]">{item.label}</p>
                  <p className="mt-1 text-[11px] text-[var(--hc-muted)]">Signal {item.signal > 0 ? "+" : ""}{item.signal} · contribution {item.weighted_score > 0 ? "+" : ""}{item.weighted_score}</p>
                </div>
              ))}
            </div>
          ) : null}
        </Panel>
      </section>

      <section id="etf-flows" aria-labelledby="etf-flows-heading" className="space-y-4">
        <Panel>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--hc-muted)]">Institutional demand</p>
              <h2 id="etf-flows-heading" className="mt-1 text-xl font-semibold tracking-[-0.025em] text-[var(--hc-text)]">BTC ETF inflow / outflow</h2>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-[var(--hc-muted)]">Daily net flows shown around zero so persistent accumulation and distribution read like the RORO regime above.</p>
            </div>
            <FreshnessTag status={markets.etf_source.status} source={markets.etf_source.name} collectedAt={markets.etf_source.collected_at} />
          </div>
          <div className="mb-4 grid gap-2 sm:grid-cols-3">
            <FlowMetric label="Latest session" value={metrics.latest} />
            <FlowMetric label="4-day mean" value={metrics.average4d} />
            <FlowMetric label="30-day cumulative" value={metrics.cumulative30d} />
          </div>
          {etfFlows.length ? <EtfFlowChart points={etfFlows} /> : <EmptyState>ETF flows will appear after a validated SoSoValue or CoinGlass response.</EmptyState>}
        </Panel>
      </section>

      <section id="bid-ask" aria-labelledby="bid-ask-heading" className="space-y-4">
        <Panel>
          <PanelHeader title="BTC bid / ask depth" meta="Binance spot · 4h" />
          <p id="bid-ask-heading" className="sr-only">BTC bid and ask depth</p>
          <p className="mb-4 max-w-3xl text-xs leading-5 text-[var(--hc-muted)]">Historical depth imbalance across ±5% and ±10% bands, followed by the latest book snapshot.</p>
          <BidAskDepthWidget />
        </Panel>
      </section>

      <LongShortWidget />

      <LeverageRegimeWidget />

      <LiquidationDashboard initialData={crypto.liquidations} />
    </div>
  );
}
